// src/pages/Auditorias/hooks/useAuditorias.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { listarAuditorias, listarAdvogados, executarAuditoria, removerAuditoria } from '../api';
import { estaEmCurso } from '../utils/formatacao';

/**
 * Lista de auditorias com filtros e disparo de novas execuções.
 *
 * Sem polling automático — o ecrã é usado por duas pessoas e recarregar sozinho
 * dava mais dano (perder a posição do scroll, fechar o que se estava a ler) do
 * que valor. O botão "actualizar" faz-lo quando se quer.
 */
export function useAuditorias() {
  const [auditorias, setAuditorias] = useState([]);
  const [advogados, setAdvogados] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [filtros, setFiltros] = useState({ advogado: '', estado: '', validacao: '', procura: '' });

  // Evita mostrar o spinner nos recarregamentos automáticos
  const primeiraCarga = useRef(true);

  const carregar = useCallback(async () => {
    try {
      const activos = Object.fromEntries(
        Object.entries(filtros).filter(([, v]) => v !== '' && v !== null)
      );
      const { data } = await listarAuditorias(activos);
      setAuditorias(data.data || []);
      setErro(null);
    } catch (e) {
      console.error('Erro ao carregar auditorias:', e);
      setErro(e.response?.data?.message || 'Não foi possível carregar as auditorias');
    } finally {
      setCarregando(false);
      primeiraCarga.current = false;
    }
  }, [filtros]);

  useEffect(() => {
    if (primeiraCarga.current) setCarregando(true);
    carregar();
  }, [carregar]);

  useEffect(() => {
    listarAdvogados()
      .then(({ data }) => setAdvogados(data.data || []))
      .catch((e) => console.error('Erro ao carregar advogados:', e));
  }, []);

  const haTrabalho = auditorias.some((a) => estaEmCurso(a.estado));

  const actualizarFiltro = useCallback((campo, valor) => {
    setFiltros((f) => ({ ...f, [campo]: valor }));
  }, []);

  const disparar = useCallback(async (payload) => {
    const { data } = await executarAuditoria(payload);
    await carregar();
    return data.data;
  }, [carregar]);

  /** Apaga a auditoria e recarrega a lista. Irreversível — confirmar antes. */
  const remover = useCallback(async (id) => {
    const { data } = await removerAuditoria(id);
    await carregar();
    return data.data;
  }, [carregar]);

  return {
    auditorias,
    advogados,
    carregando,
    erro,
    filtros,
    actualizarFiltro,
    recarregar: carregar,
    disparar,
    remover,
    haTrabalho,
  };
}
